/**
 * Review System offer landing page (Sprint 3B).
 *
 * Explains the review request engine without pressure, without
 * competitor bashing, and without any "guarantee" language.
 * Reachable from /offers/reviews and /offers/reviews/:businessId.
 *
 * The primary CTA records a `service_requests` row with
 * offerType = REVIEW_SYSTEM_INTEREST. Repeat clicks within a short
 * window are deduped server-side.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import {
  getOfferBusinessContext,
  createOfferInterest,
} from "@/lib/api";

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "We check in after service",
    body: "A short, personal message goes out after the work is done — the right moment to hear how it went.",
  },
  {
    step: "2",
    title: "Customers share their experience",
    body: "The first message is about listening, not asking for a review. Customers tell you how it really went.",
  },
  {
    step: "3",
    title: "Businesses can respond to concerns",
    body: "If something wasn't quite right, you hear about it early — before it becomes a bad public review.",
  },
  {
    step: "4",
    title: "Happy customers can share their experience publicly",
    body: "Customers who want to share their experience are invited to leave a public review. No pressure, no filtering.",
  },
];

export default function ReviewSystemOffer() {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const [businessName, setBusinessName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!businessId) return;
    (async () => {
      try {
        const data = await getOfferBusinessContext(businessId);
        if (alive) setBusinessName(data?.business?.businessName || "");
      } catch {
        // Non-blocking — the page still renders.
      }
    })();
    return () => {
      alive = false;
    };
  }, [businessId]);

  const goBack = () => {
    if (businessId) navigate(`/onboarding/results/${businessId}`);
    else navigate(-1);
  };

  const onCtaClick = async () => {
    if (!businessId) {
      toast.info(
        "Thanks for your interest. Complete your Monday Brief signup so we can send you tailored pricing."
      );
      return;
    }
    setSubmitting(true);
    try {
      await createOfferInterest({
        businessId,
        offerType: "REVIEW_SYSTEM_INTEREST",
        sourcePage: `/offers/reviews/${businessId}`,
      });
      setSubmitted(true);
      toast.success(
        "Thanks — we'll be in touch. No payment or account changes were made."
      );
    } catch {
      toast.error(
        "We couldn't record your request. Please try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div
        className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14 space-y-10"
        data-testid="review-system-offer-page"
      >
        <button
          data-testid="review-system-offer-back"
          onClick={goBack}
          className="text-sm underline"
          style={{ color: "var(--ta-text-secondary)" }}
        >
          ← Back
        </button>

        <header className="space-y-4">
          <div
            className="inline-block text-xs uppercase tracking-widest px-3 py-1 rounded-full"
            style={{
              background: "var(--ta-surface-alt)",
              color: "var(--ta-text-secondary)",
              border: "1px solid var(--ta-border)",
            }}
            data-testid="review-system-offer-eyebrow"
          >
            Reputation System · From Your Monday Brief
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight"
            style={{ color: "var(--ta-primary)" }}
            data-testid="review-system-offer-headline"
          >
            Turn happy customers into reviews — while catching problems
            before they become bad reviews.
          </h1>
          <p
            className="text-base sm:text-lg leading-relaxed"
            style={{ color: "var(--ta-text-secondary)" }}
            data-testid="review-system-offer-subhead"
          >
            We help businesses stay connected with their customers after a
            service is completed. The goal is simple: understand the customer
            experience, fix issues quickly, and make it easier for satisfied
            customers to share their feedback.
          </p>
          <p
            className="text-base italic leading-relaxed"
            style={{ color: "var(--ta-primary)" }}
            data-testid="review-system-offer-core-message"
          >
            &ldquo;Great businesses don&apos;t just ask for reviews. They
            listen to their customers first.&rdquo;
          </p>
        </header>

        {/* How it works. */}
        <section
          className="space-y-5"
          data-testid="review-system-offer-how-it-works"
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: "var(--ta-primary)" }}
          >
            How it works
          </h2>
          <ol className="space-y-4">
            {HOW_IT_WORKS.map((item) => (
              <li
                key={item.step}
                className="rounded-2xl p-5 flex gap-4"
                style={{
                  background: "var(--ta-surface)",
                  border: "1px solid var(--ta-border)",
                }}
              >
                <div
                  className="flex-none w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
                  style={{
                    background: "var(--ta-primary)",
                    color: "white",
                  }}
                >
                  {item.step}
                </div>
                <div className="flex-1">
                  <div
                    className="text-sm font-semibold mb-1"
                    style={{ color: "var(--ta-primary)" }}
                  >
                    {item.title}
                  </div>
                  <div
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--ta-text-secondary)" }}
                  >
                    {item.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Sample customer message — shows the owner exactly what
            customers receive, framed as listening first. */}
        <section
          className="space-y-4"
          data-testid="review-system-offer-sample-message"
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: "var(--ta-primary)" }}
          >
            Example customer message
          </h2>
          <div
            className="rounded-2xl p-5"
            style={{
              background: "var(--ta-surface)",
              border: "1px solid var(--ta-border)",
            }}
          >
            <div
              className="text-xs uppercase tracking-widest mb-2"
              style={{ color: "var(--ta-text-secondary)" }}
            >
              What your customer sees
            </div>
            <p
              className="text-sm leading-relaxed"
              style={{ color: "var(--ta-primary)" }}
              data-testid="review-system-offer-sample-body"
            >
              Hi Sarah, thanks again for trusting us with your recent service.
              We hope everything went well. We would love to hear how your
              experience was. Is there anything we could have done better?
            </p>
          </div>
          <p
            className="text-sm leading-relaxed"
            style={{ color: "var(--ta-text-secondary)" }}
            data-testid="review-system-offer-sample-explanation"
          >
            This first message is about listening, not asking for a review.
          </p>
          <p
            className="text-sm leading-relaxed"
            style={{ color: "var(--ta-text-secondary)" }}
            data-testid="review-system-offer-sample-followup"
          >
            If the customer had a great experience, they can be invited to
            share their feedback publicly.
          </p>
          <p
            className="text-xs italic leading-relaxed"
            style={{ color: "var(--ta-text-secondary)" }}
          >
            We ask customers for feedback and make it easier for those who
            want to share their experience publicly.
          </p>
        </section>

        {/* Already using a review system? */}
        <section
          className="rounded-2xl p-6 space-y-3"
          style={{
            background: "var(--ta-surface-alt)",
            border: "1px solid var(--ta-border)",
          }}
          data-testid="review-system-offer-already-using"
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: "var(--ta-primary)" }}
          >
            Already using a review system?
          </h2>
          <p
            className="text-sm leading-relaxed"
            style={{ color: "var(--ta-text-secondary)" }}
          >
            If you already have a review system or other marketing tools,
            compare what you are paying. Many businesses are paying more than
            necessary for similar tools. Our goal is to provide effective
            solutions at a fair price with straightforward pricing.
          </p>
        </section>

        {/* Short video placeholder. */}
        <div
          data-testid="review-system-offer-video-placeholder"
          className="w-full aspect-video rounded-2xl flex items-center justify-center"
          style={{
            background: "var(--ta-surface-alt)",
            border: "1px dashed var(--ta-border)",
            color: "var(--ta-text-secondary)",
          }}
        >
          <div className="text-center px-6">
            <div
              className="text-xs uppercase tracking-widest mb-2"
              style={{ color: "var(--ta-text-secondary)" }}
            >
              Short video coming soon
            </div>
            <div className="text-sm">
              A walkthrough of how the review flow feels for your customers
              will live here.
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center space-y-3">
          {businessName && (
            <p
              className="text-sm"
              style={{ color: "var(--ta-text-secondary)" }}
              data-testid="review-system-offer-business-name"
            >
              For {businessName}
            </p>
          )}
          <button
            data-testid="review-system-offer-cta"
            onClick={onCtaClick}
            disabled={submitting || submitted}
            className="px-8 py-3 rounded-full text-sm font-semibold disabled:opacity-60"
            style={{ background: "var(--ta-primary)", color: "white" }}
          >
            {submitted
              ? "Request received — we'll be in touch"
              : submitting
              ? "Sending your request…"
              : "Request pricing"}
          </button>
          <p
            className="text-xs"
            style={{ color: "var(--ta-text-secondary)" }}
          >
            No payment or account changes happen when you click this.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
